import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const CostBreakdown = ({ costs, isCalculating }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value);
  };

  const costItems = [
    {
      label: 'Material',
      value: costs?.material,
      icon: 'Package',
      color: 'text-primary',
      bgColor: 'bg-primary/10'
    },
    {
      label: 'Energia elétrica',
      value: costs?.electricity,
      icon: 'Zap',
      color: 'text-warning',
      bgColor: 'bg-warning/10'
    },
    {
      label: 'Depreciação',
      value: costs?.depreciation,
      icon: 'TrendingDown',
      color: 'text-secondary',
      bgColor: 'bg-secondary/10'
    },
    {
      label: 'Mão de obra',
      value: costs?.labor,
      icon: 'Clock',
      color: 'text-accent',
      bgColor: 'bg-accent/10'
    }
  ];

  if (isCalculating) {
    return (
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Calculando custos...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="bg-card rounded-2xl border border-border p-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
          <Icon name="Calculator" size={20} className="text-success" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Breakdown de Custos</h3>
          <p className="text-sm text-muted-foreground">Análise detalhada dos gastos</p>
        </div>
      </div>
      {/* Total Cost */}
      <motion.div 
        className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-6 mb-6"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <div className="text-center">
          <div className="text-sm text-muted-foreground mb-1">Custo Total</div>
          <div className="text-3xl font-bold text-foreground">
            {formatCurrency(costs?.total)}
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            Por unidade: {formatCurrency(costs?.perUnit)}
          </div>
        </div>
      </motion.div>
      {/* Cost Breakdown */}
      <div className="space-y-4">
        {costItems?.map((item, index) => (
          <motion.div
            key={item?.label}
            className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2, delay: index * 0.1 }}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 ${item?.bgColor} rounded-lg flex items-center justify-center`}>
                <Icon name={item?.icon} size={16} className={item?.color} />
              </div>
              <span className="font-medium text-foreground">{item?.label}</span>
            </div>
            <div className="text-right">
              <div className="font-semibold text-foreground">{formatCurrency(item?.value)}</div>
              <div className="text-xs text-muted-foreground">
                {((item?.value / costs?.total) * 100)?.toFixed(1)}%
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      {/* Additional Info */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="text-center">
            <div className="text-muted-foreground">Tempo estimado</div>
            <div className="font-medium text-foreground">{costs?.estimatedTime}h</div>
          </div>
          <div className="text-center">
            <div className="text-muted-foreground">Material usado</div>
            <div className="font-medium text-foreground">{costs?.materialUsed}g</div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CostBreakdown;